import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { Calendar, Printer } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

function currentMonth() { return new Date().toISOString().slice(0,7); }
function todayIso() { return new Date().toISOString().slice(0,10); }
function daysAgoIso(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0,10); }

export default function Reconciliation() {
  const [mode, setMode] = useState("month"); // "month" | "range"
  const [month, setMonth] = useState(currentMonth());
  const [dateFrom, setDateFrom] = useState(daysAgoIso(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [d, setD] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = mode === "range"
      ? { date_from: dateFrom, date_to: dateTo }
      : { month };
    const query = new URLSearchParams(params).toString();
    api.get(`/reports/reconciliation?${query}`)
      .then(r => setD(r.data))
      .finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, [mode, month, dateFrom, dateTo]);
  useEffect(() => { api.get("/vendors").then(r => setVendors(r.data || [])); }, []);

  const applyPreset = (days) => {
    setMode("range");
    setDateFrom(daysAgoIso(days));
    setDateTo(todayIso());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1 flex items-center gap-1"><Calendar size={11}/> Reports</div>
          <h1 className="font-display text-3xl tracking-tight">Reconciliation</h1>
          <p className="text-sm text-[#75706A] mt-1">Material in, sent for customization, back, and dispatched — for a month or custom range.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-full border border-[#E7E1D3] p-0.5 bg-white" data-testid="recon-mode">
            <button onClick={()=>setMode("month")} data-testid="mode-month"
              className={`px-3 py-1 text-xs rounded-full transition ${mode==="month"?"bg-[#C45A41] text-white":"text-[#75706A]"}`}>Monthly</button>
            <button onClick={()=>setMode("range")} data-testid="mode-range"
              className={`px-3 py-1 text-xs rounded-full transition ${mode==="range"?"bg-[#C45A41] text-white":"text-[#75706A]"}`}>Custom range</button>
          </div>
          {mode === "month" ? (
            <input type="month" className="input-field w-48" value={month} onChange={e=>setMonth(e.target.value)} data-testid="recon-month"/>
          ) : (
            <>
              <input type="date" className="input-field w-40" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} data-testid="recon-date-from"/>
              <span className="text-xs text-[#75706A]">to</span>
              <input type="date" className="input-field w-40" value={dateTo} onChange={e=>setDateTo(e.target.value)} data-testid="recon-date-to"/>
            </>
          )}
        </div>
      </div>

      {mode === "range" && (
        <div className="flex gap-1.5 flex-wrap">
          <button className="btn-ghost text-xs" onClick={()=>applyPreset(7)} data-testid="preset-7d">Last 7 days</button>
          <button className="btn-ghost text-xs" onClick={()=>applyPreset(30)} data-testid="preset-30d">Last 30 days</button>
          <button className="btn-ghost text-xs" onClick={()=>applyPreset(90)} data-testid="preset-90d">Last 90 days</button>
          <button className="btn-ghost text-xs" onClick={()=>applyPreset(365)} data-testid="preset-1y">Last 1 year</button>
        </div>
      )}

      {loading && <div className="text-[#75706A] text-sm">Loading…</div>}
      {!loading && d && (
        <>
          <div className="text-xs text-[#75706A]">Window: <span className="mono">{d.window}</span></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Material received (pcs)" value={d.material_received_pcs} />
            <Kpi label="Jobs sent to customization" value={Object.values(d.sent_to_customization_by_unit).reduce((a,b)=>a+b,0)} />
            <Kpi label="Received back (jobs)" value={d.received_back_jobs} />
            <Kpi label="Dispatched (pcs)" value={d.dispatched_pcs} />
          </div>
          <div className="card-surface p-5">
            <div className="label-tiny mb-3">Sent for customization — by unit</div>
            {Object.keys(d.sent_to_customization_by_unit).length === 0 ? (
              <div className="text-sm text-[#75706A]">No jobs sent in this window.</div>
            ) : (
              <table className="data-table">
                <thead><tr><th>Customization unit</th><th>Jobs</th></tr></thead>
                <tbody>
                  {Object.entries(d.sent_to_customization_by_unit).map(([k,v]) => (
                    <tr key={k}><td>{k || "(unspecified)"}</td><td className="mono">{v}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detailed jobs table — mirrors OrderDetail Production tab */}
          <div className="card-surface p-5" data-testid="recon-jobs-detail">
            <div className="label-tiny mb-3">Customization jobs — detail</div>
            {(!d.customization_jobs || d.customization_jobs.length === 0) ? (
              <div className="text-sm text-[#75706A]">No customization jobs in this window.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr>
                    <th>Order</th><th>Customer</th><th>Vendor</th><th>Type</th><th>Sent</th><th>Rec.</th><th>Damaged</th><th>Cost</th><th>Status</th><th>Job Card</th>
                  </tr></thead>
                  <tbody>
                    {d.customization_jobs.map(c => {
                      const damaged = Number(c.qty_damaged || 0) > 0;
                      const done = !!c.completed_at;
                      const jobUrl = `${window.location.origin}/print/job-card/${c.order_id}?job=${c.id}`;
                      const vend = vendors.find(v => (v.name||"").toLowerCase() === (c.vendor||"").toLowerCase());
                      const jobMsg = `Job Card ${c.order_no} — ${(c.type||"").toUpperCase()}\n${c.qty_sent || "?"} pcs\n\nOpen: ${jobUrl}`;
                      return (
                        <tr key={c.id} className={damaged ? "bg-[#FBEBE7]" : ""} data-testid={`recon-job-${c.id}`}>
                          <td><Link to={`/orders/${c.order_id}`} className="mono hover:underline">{c.order_no}</Link></td>
                          <td className="text-xs">{c.customer_name}</td>
                          <td>{vend?.id ? <Link to={`/vendors/${vend.id}`} className="hover:underline">{c.vendor}</Link> : (c.vendor || "—")}</td>
                          <td className="capitalize">{c.type}</td>
                          <td className="mono">{c.qty_sent || "—"}</td>
                          <td className="mono">{c.qty_received || "—"}</td>
                          <td className={`mono ${damaged ? "text-[#B83A3A] font-bold" : ""}`} title={c.damage_notes||""}>{c.qty_damaged || "—"}</td>
                          <td className="mono">{inr(c.cost)}</td>
                          <td>{done ? <span className="badge badge-success">Done</span> : <span className="badge badge-warn">In progress</span>}</td>
                          <td>
                            <div className="flex flex-wrap gap-1.5">
                              <a href={jobUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1" title="Preview Job Card"><Printer size={11}/>Preview</a>
                              {vend?.phone && <WhatsAppButton phone={vend.phone} message={jobMsg} label="Send" size="sm" testid={`recon-wa-${c.id}`}/>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const Kpi = ({label, value}) => (
  <div className="card-surface p-4">
    <div className="label-tiny">{label}</div>
    <div className="font-display text-3xl font-bold mt-1">{value}</div>
  </div>
);
